import { useState, useCallback, useMemo } from "react"
import { api } from "../../../lib/api/client"
import { throttle } from "../../../lib/utils"
import type { SearchOption, DisplayMode, CollectionSearchResponse, CustomSearchFn } from "./types"
import type { ReactNode } from "react"

/**
 * Format a phone number for display
 */
function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/**
 * Capitalize first letter of each word
 */
function capitalize(str: string): string {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

/**
 * Get the default label from a document
 */
function getDefaultLabel(doc: any, includeFields?: string[]): string {
  let label = doc.name || ""
  let emailIsIdentifier = false

  if (doc.display_name) {
    label = doc.display_name
  } else if (doc.given_name && doc.family_name) {
    label = capitalize(`${doc.given_name} ${doc.family_name}`)
  } else if (!label && doc.email) {
    label = doc.email
    emailIsIdentifier = true
  } else if (!label && doc.phone) {
    label = formatPhone(doc.phone)
  }

  // Append email if includeFields specifies it and it wasn't used as identifier
  if (!emailIsIdentifier && includeFields?.includes("email") && doc.email) {
    label += ` - ${doc.email}`
  }

  return label
}

interface UseCollectionSearchOptions {
  collection: string
  filter?: Record<string, any>
  sort?: Record<string, 1 | -1>
  limit?: number
  displayMode?: DisplayMode
  formatLabel?: (item: any) => ReactNode
  includeFields?: string[]
  addNull?: boolean
  customSearchFn?: CustomSearchFn
}

interface UseCollectionSearchResult {
  options: SearchOption[]
  isLoading: boolean
  error: Error | null
  search: (query: string) => void
  loadInitial: () => void
}

/**
 * Hook for searching a collection with throttling and enhanced display modes.
 */
export function useCollectionSearch({
  collection,
  filter,
  sort,
  limit = 20,
  displayMode = "default",
  formatLabel,
  includeFields,
  addNull,
  customSearchFn,
}: UseCollectionSearchOptions): UseCollectionSearchResult {
  const [options, setOptions] = useState<SearchOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)


  /**
   * Transform API documents to SearchOption format
   */
  const transformDocuments = useCallback(
    (documents: any[]): SearchOption[] => {
      const results: SearchOption[] = []

      // Add null option if requested
      if (addNull) {
        results.push({
          value: "",
          label: (
            <span className="flex items-center text-danger">
              <span className="mr-2">✕</span>
              None
            </span>
          ),
          data: { _id: null, name: "None" },
        })
      }

      documents.forEach((doc) => {
        let label: ReactNode

        // Custom formatter takes precedence
        if (formatLabel) {
          label = formatLabel(doc)
        } else if (displayMode === "users") {
          // Enhanced user display with title and availability
          const name = getDefaultLabel(doc, includeFields)
          const canReceiveCalls = doc.agent_can_receive_calls
          const onCall = doc.agent_current_call

          // Get title from division override if division is specified in filter
          // Handle various filter formats: { division: id }, { divisions: id }, { divisions: { $in: [id] } }
          let divisionId: string | null = null
          if (filter?.division) {
            divisionId = filter.division
          } else if (filter?.divisions) {
            if (typeof filter.divisions === "string") {
              divisionId = filter.divisions
            } else if (filter.divisions?.$in?.[0]) {
              divisionId = filter.divisions.$in[0]
            }
          }
          const divisionOverride = divisionId
            ? doc.division_overrides?.find(
                (d: { division: string; title?: string }) => d.division === divisionId
              )
            : null
          const title = divisionOverride?.title

          label = (
            <div className="flex w-full items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{name}</div>
                {title && (
                  <div className="truncate text-xs text-muted-foreground">{title}</div>
                )}
              </div>
              <div className="ml-2 flex-shrink-0">
                {canReceiveCalls && !onCall ? (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success text-white">
                    <span className="text-xs">✓</span>
                  </span>
                ) : canReceiveCalls && onCall ? (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-warning text-white">
                    <span className="text-xs">📞</span>
                  </span>
                ) : (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-danger text-white">
                    <span className="text-xs">✕</span>
                  </span>
                )}
              </div>
            </div>
          )
        } else if (displayMode === "contacts") {
          // Enhanced contact display with email and phone
          const displayName = doc.display_name
          const email = doc.email
          const phone = doc.phone ? formatPhone(doc.phone) : null

          label = (
            <div>
              {displayName ? (
                <>
                  <div className="font-medium">{displayName}</div>
                  <div className="text-xs text-muted-foreground">
                    {email ? `${email}${phone ? " - " : ""}` : ""}
                    {phone || ""}
                  </div>
                </>
              ) : (
                <div>
                  {email ? `${email}${phone ? " - " : ""}` : ""}
                  {phone || ""}
                </div>
              )}
            </div>
          )
        } else if (displayMode === "phone_numbers") {
          // Phone number display
          label = (
            <div>
              {doc.name} - {formatPhone(doc.number || doc.phone || "")}
            </div>
          )
        } else {
          // Default display
          label = getDefaultLabel(doc, includeFields)
        }

        results.push({
          value: doc._id,
          label,
          data: doc,
        })
      })

      return results
    },
    [addNull, displayMode, filter, formatLabel, includeFields]
  )

  /**
   * Perform the search API call
   */
  const performSearch = useCallback(
    async (query: string) => {
      setIsLoading(true)
      setError(null)

      try {
        // Build search params
        const searchParams = {
          searchString: query,
          filter: filter || {},
          limit,
          ...(sort && { sort }),
        }

        // Use custom search function if provided, otherwise use collection API
        let response: CollectionSearchResponse
        if (customSearchFn) {
          response = await customSearchFn(searchParams)
        } else {
          const apiMethod = (api as any)[collection]?.search
          if (!apiMethod) {
            throw new Error(`No search method found for collection: ${collection}`)
          }
          response = await apiMethod(searchParams)
        }

        if (!response.success && !response.data) {
          setOptions([
            {
              value: "error",
              label: (
                <span className="flex items-center text-danger">
                  <span className="mr-2">✕</span>
                  Something went wrong, please try again
                </span>
              ),
              data: null,
              disabled: true,
            },
          ])
          return
        }

        const documents = response.data?.documents || response.data || []
        const transformed = transformDocuments(documents)
        setOptions(transformed)
      } catch (err) {
        setError(err as Error)
        setOptions([
          {
            value: "error",
            label: (
              <span className="flex items-center text-danger">
                <span className="mr-2">✕</span>
                Something went wrong, please try again
              </span>
            ),
            data: null,
            disabled: true,
          },
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [collection, customSearchFn, filter, limit, sort, transformDocuments]
  )

  /**
   * Throttled search function - runs after 300ms or after 3 calls
   */
  const throttledSearch = useMemo(
    () => throttle(performSearch, 300, 3),
    [performSearch]
  )

  /**
   * Search function to be called on input change
   */
  const search = useCallback(
    (query: string) => {
      throttledSearch(query)
    },
    [throttledSearch]
  )

  /**
   * Load initial options (empty search) - always fetches fresh results
   */
  const loadInitial = useCallback(() => {
    performSearch("")
  }, [performSearch])

  return {
    options,
    isLoading,
    error,
    search,
    loadInitial,
  }
}
